import { useCallback } from 'react';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import type { ConnectorSide, ConnectionDragState, RedockDragState } from '../types';
import { getConnectorPointForRect, screenToCanvas, resolveEndpointPosition } from '../types';
import { useCanvasStores, useCanvasRotation } from '../stores/CanvasContext';
import type { RedockDragRefValue } from '../stores/CanvasContext';
import type { CanvasStore } from '../stores/canvasStore';
import type { CanvasDragStore } from '../stores/canvasDragStore';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { buildEntityRectMap } from '../lib/buildEntityRectMap';
import { buildValidEntityIds } from '../lib/buildValidEntityIds';
import type { ConnectorEndpoint } from '../entityTypes';

/**
 * Build a connector segments map from store snapshot.
 */
function buildConnectorSegmentsMap(store: CanvasStore) {
  const ctx = store.getSnapshot().context;
  const gtCtx = globalTasksStore.getSnapshot().context;
  const rawNodes = Object.values(ctx.nodes);
  const validEntityIds = buildValidEntityIds(rawNodes, gtCtx.tasksById);

  const map = new Map<string, Array<{ segmentId: string; end: 'from' | 'to' }>>();
  function addKey(key: string, segmentId: string, end: 'from' | 'to') {
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push({ segmentId, end });
  }

  interface SegmentLike {
    id: string;
    from: { entityId: string; side: ConnectorSide | 'center' };
    to: { entityId: string; side: ConnectorSide | 'center' };
  }
  const rawConnectors = Object.values(ctx.connectors);
  const segments: SegmentLike[] = [];
  for (const e of rawConnectors) {
    const cd = e.data as unknown as { from: SegmentLike['from']; to: SegmentLike['to']; segmentType: string };
    if (!validEntityIds.has(cd.from.entityId) || !validEntityIds.has(cd.to.entityId)) continue;
    const seg = { id: e.id, from: cd.from, to: cd.to };
    segments.push(seg);
    for (const [ep, end] of [[seg.from, 'from'], [seg.to, 'to']] as const) {
      addKey(`${ep.entityId}:${ep.side}`, seg.id, end);
    }
  }

  return { connectorSegmentsMap: map, segments };
}

function buildEntityRectMapFromStore(store: CanvasStore) {
  const ctx = store.getSnapshot().context;
  const gtCtx = globalTasksStore.getSnapshot().context;
  const rawNodes = Object.values(ctx.nodes);
  return buildEntityRectMap(rawNodes, gtCtx.tasksById);
}

/** Build RedockDragState, set ref, and dispatch to dragStore. */
function startRedockDrag(
  end: 'from' | 'to',
  anchorEndpoint: ConnectorEndpoint,
  anchorPoint: { x: number; y: number },
  canvasPoint: { x: number; y: number },
  segmentId: string,
  selectedEdgeIds: string[],
  redockDragRef: React.MutableRefObject<RedockDragRefValue | null>,
  dragStore: CanvasDragStore,
): void {
  const redockState: RedockDragState = {
    end,
    anchorEndpoint,
    anchorPoint,
    currentPoint: canvasPoint,
  };
  redockDragRef.current = { state: redockState, segmentId, end, selectedEdgeIds };
  dragStore.send({ type: 'redockDrag.set', drag: redockState, dropTarget: null });
}

/**
 * Pure callback factory — no side-effects.
 * Writes to connectionDragRef/redockDragRef from context to start drags.
 * All data is read inline from store snapshots — no reactive subscriptions.
 */
export function useCanvasConnectorOps() {
  const { store, containerRef, panZoomStore, dragStore, connectionDragRef, redockDragRef } = useCanvasStores();
  const rotation = useCanvasRotation();

  const handleConnectorMouseDown = useCallback((entityId: string, side: ConnectorSide, e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const pz = panZoomStore.getSnapshot().context;
    const container = containerRef.current;
    if (!container) return;

    const canvasPoint = screenToCanvas(e.clientX, e.clientY, container, pz, rotation);
    const entityRectMap = buildEntityRectMapFromStore(store);
    const rect = entityRectMap.get(entityId);
    const fromPoint = rect
      ? getConnectorPointForRect(rect.x, rect.y, rect.width, rect.height, side)
      : canvasPoint;

    const drag: ConnectionDragState = {
      fromEndpoint: { entityId, side },
      fromPoint,
      currentPoint: canvasPoint,
    };
    connectionDragRef.current = drag;
    dragStore.send({ type: 'connectionDrag.set', drag, dropTarget: null });
  }, [store, panZoomStore, rotation, containerRef, connectionDragRef, dragStore]);

  const handleConnectorRedock = useCallback((entityId: string, side: ConnectorSide, e: React.PointerEvent) => {
    const { connectorSegmentsMap, segments } = buildConnectorSegmentsMap(store);

    const key = `${entityId}:${side}`;
    const segmentsHere = connectorSegmentsMap.get(key);
    if (!segmentsHere || segmentsHere.length === 0) return;

    const selEdgeIds = canvasSelectionStore.getSnapshot().context.selectedEntityIds;
    const selectedHere = segmentsHere.filter(entry => selEdgeIds.includes(entry.segmentId));
    const toRedock = selectedHere.length > 0 ? selectedHere : segmentsHere;

    const primary = toRedock[0];
    const sameEnd = toRedock.filter(entry => entry.end === primary.end);

    canvasSelectionStore.send({ type: 'entity.set', entityIds: sameEnd.map(entry => entry.segmentId) });

    e.stopPropagation();
    e.preventDefault();

    const segment = segments.find(s => s.id === primary.segmentId);
    if (!segment) return;

    const entityRectMap = buildEntityRectMapFromStore(store);
    const anchorEndpoint = primary.end === 'from' ? segment.to : segment.from;
    const anchorPoint = resolveEndpointPosition(anchorEndpoint, entityRectMap);
    if (!anchorPoint) return;

    const container = containerRef.current;
    if (!container) return;
    const pz = panZoomStore.getSnapshot().context;
    const canvasPoint = screenToCanvas(e.clientX, e.clientY, container, pz, rotation);

    const edgeIds = selEdgeIds.includes(primary.segmentId)
      ? sameEnd.map(entry => entry.segmentId)
      : [primary.segmentId];

    startRedockDrag(primary.end, anchorEndpoint, anchorPoint, canvasPoint, primary.segmentId, edgeIds, redockDragRef, dragStore);
  }, [store, containerRef, panZoomStore, rotation, redockDragRef, dragStore]);

  const handleWaypointConnectorMouseDown = useCallback((waypointId: string, e: React.PointerEvent) => {
    const ctx = store.getSnapshot().context;
    const entity = ctx.nodes[waypointId];
    if (!entity) return;

    e.stopPropagation();
    e.preventDefault();

    const container = containerRef.current;
    if (!container) return;
    const pz = panZoomStore.getSnapshot().context;
    const canvasPoint = screenToCanvas(e.clientX, e.clientY, container, pz, rotation);

    const drag: ConnectionDragState = {
      fromEndpoint: { entityId: waypointId, side: 'center' },
      fromPoint: { x: entity.x, y: entity.y },
      currentPoint: canvasPoint,
    };
    connectionDragRef.current = drag;
    dragStore.send({ type: 'connectionDrag.set', drag, dropTarget: null });
  }, [store, containerRef, panZoomStore, rotation, connectionDragRef, dragStore]);

  const handleSegmentDragStart = useCallback((segmentId: string, e: React.PointerEvent) => {
    if (!containerRef.current) return;

    const entityRectMap = buildEntityRectMapFromStore(store);

    // Find the segment from store snapshot
    const ctx = store.getSnapshot().context;
    const rawNodes = Object.values(ctx.nodes);
    const rawConnectors = Object.values(ctx.connectors);
    const validEntityIds = buildValidEntityIds(rawNodes, globalTasksStore.getSnapshot().context.tasksById);

    const connectorEntity = rawConnectors.find(ent => ent.id === segmentId);
    if (!connectorEntity) return;
    const cd = connectorEntity.data as unknown as { from: { entityId: string; side: ConnectorSide | 'center' }; to: { entityId: string; side: ConnectorSide | 'center' } };
    if (!validEntityIds.has(cd.from.entityId) || !validEntityIds.has(cd.to.entityId)) return;
    const segment = { id: segmentId, from: cd.from, to: cd.to };

    const fromPt = resolveEndpointPosition(segment.from, entityRectMap);
    const toPt = resolveEndpointPosition(segment.to, entityRectMap);
    if (!fromPt || !toPt) return;

    const pz = panZoomStore.getSnapshot().context;
    const clickCanvas = screenToCanvas(e.clientX, e.clientY, containerRef.current, pz, rotation);

    const distFrom = Math.hypot(clickCanvas.x - fromPt.x, clickCanvas.y - fromPt.y);
    const distTo = Math.hypot(clickCanvas.x - toPt.x, clickCanvas.y - toPt.y);
    const end: 'from' | 'to' = distFrom <= distTo ? 'from' : 'to';

    const selEdgeIds = canvasSelectionStore.getSnapshot().context.selectedEntityIds;
    if (!selEdgeIds.includes(segmentId)) {
      canvasSelectionStore.send({ type: 'entity.selectOne', entityId: segmentId });
    }

    e.stopPropagation();
    e.preventDefault();

    const anchorEndpoint = end === 'from' ? segment.to : segment.from;
    const anchorPoint = resolveEndpointPosition(anchorEndpoint, entityRectMap);
    if (!anchorPoint) return;

    const canvasPoint = screenToCanvas(e.clientX, e.clientY, containerRef.current, pz, rotation);
    const edgeIds = selEdgeIds.includes(segmentId) ? selEdgeIds : [segmentId];

    startRedockDrag(end, anchorEndpoint, anchorPoint, canvasPoint, segmentId, edgeIds, redockDragRef, dragStore);
  }, [store, panZoomStore, rotation, containerRef, redockDragRef, dragStore]);

  return {
    handleConnectorMouseDown,
    handleConnectorRedock,
    handleWaypointConnectorMouseDown,
    handleSegmentDragStart,
  };
}
